/**
 * GalleryItem — the frame around one card specimen on the hidden /test page.
 *
 * Every entry states the same three things: what the component is called, where
 * it lives, and which screens actually render it. The preview sits on a muted
 * canvas so the card's own surface/border/shadow reads honestly against a
 * non-white background.
 */
import ContentCopyOutlined from "@mui/icons-material/ContentCopyOutlined";
import { Box, Chip, IconButton, Tooltip } from "@mui/material";
import PropTypes from "prop-types";
import { memo, useCallback } from "react";

import Flx from "../../../../components/layout/Flx";
import Txt from "../../../../components/typography/Txt";

const GalleryItem = memo(
  ({
    name,
    path,
    description,
    usedIn = [],
    variants,
    unused = false,
    canvas = "muted",
    swatchMinWidth = 260,
    children,
  }) => {
    const handleCopyPath = useCallback(() => {
      navigator.clipboard?.writeText(path);
    }, [path]);

    return (
      <Box
        id={name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}
        sx={{
          border: "1px solid #e3e9ec",
          borderRadius: "12px",
          overflow: "hidden",
          background: "#fff",
          scrollMarginTop: "16px",
        }}
      >
        {/* ── Spec header ── */}
        <Box sx={{ px: 2.5, py: 2, borderBottom: "1px solid #eef3f5" }}>
          <Flx jb ac wrap gap={2}>
            <Flx ac gap={1} wrap>
              <Txt sx={{ fontSize: 15, fontWeight: 700, color: "#0f172a" }}>
                {name}
              </Txt>
              {unused && (
                <Chip
                  label="Not currently used"
                  size="small"
                  sx={{
                    height: 19,
                    fontSize: 10.5,
                    fontWeight: 600,
                    background: "#fef3c7",
                    color: "#92400e",
                  }}
                />
              )}
              {variants && (
                <Txt sx={{ fontSize: 11.5, color: "#64748b" }}>
                  {variants}
                </Txt>
              )}
            </Flx>
            <Flx ac gap={0.5}>
              <Txt
                sx={{
                  fontFamily:
                    '"JetBrains Mono", "SF Mono", Menlo, monospace',
                  fontSize: 11,
                  color: "#475569",
                  background: "#f1f5f9",
                  border: "1px solid #e2e8f0",
                  borderRadius: "6px",
                  px: 1,
                  py: "3px",
                }}
              >
                {path}
              </Txt>
              <Tooltip title="Copy import path" arrow>
                <IconButton size="small" onClick={handleCopyPath}>
                  <ContentCopyOutlined sx={{ fontSize: 14 }} />
                </IconButton>
              </Tooltip>
            </Flx>
          </Flx>

          {description && (
            <Txt secondary sx={{ fontSize: 12.5, mt: 0.75 }}>
              {description}
            </Txt>
          )}

          {/* ── Where it's used ── */}
          <Flx ac wrap gap={0.75} sx={{ mt: 1.25 }}>
            <Txt
              sx={{
                fontSize: 10.5,
                fontWeight: 700,
                letterSpacing: "0.06em",
                textTransform: "uppercase",
                color: "#94a3b8",
                mr: 0.5,
              }}
            >
              Used in
            </Txt>
            {usedIn.length === 0 ? (
              <Txt sx={{ fontSize: 12, color: "#94a3b8", fontStyle: "italic" }}>
                nothing yet — available for reuse
              </Txt>
            ) : (
              usedIn.map((u) => (
                <Chip
                  key={u}
                  label={u}
                  size="small"
                  sx={{
                    height: 21,
                    fontSize: 11,
                    fontWeight: 500,
                    background: "#eef3f5",
                    color: "#334155",
                    border: "1px solid #e3e9ec",
                  }}
                />
              ))
            )}
          </Flx>
        </Box>

        {/* ── Swatches ──
            A muted canvas, always: a white surface on a white page is
            indistinguishable from no surface at all, which defeats the point. */}
        <Box
          sx={{
            p: 3,
            background: canvas === "white" ? "#fff" : "#f5f7f9",
            display: "grid",
            gridTemplateColumns: `repeat(auto-fit, minmax(${swatchMinWidth}px, 1fr))`,
            gap: 3,
            alignItems: "start",
          }}
        >
          {children}
        </Box>
      </Box>
    );
  },
);

GalleryItem.propTypes = {
  /** Component name as imported. */
  name: PropTypes.string.isRequired,
  /** Repo-relative source path. */
  path: PropTypes.string.isRequired,
  /** One-line description of what the card is for. */
  description: PropTypes.node,
  /** Screens / features that render this card today. */
  usedIn: PropTypes.arrayOf(PropTypes.string),
  /** Free-text list of the variants shown in the preview. */
  variants: PropTypes.string,
  /** Flag components that exist but have no current call sites. */
  unused: PropTypes.bool,
  /** Preview canvas: "muted" (default) or "white". */
  canvas: PropTypes.oneOf(["muted", "white"]),
  /** Min column width of the swatch grid. */
  swatchMinWidth: PropTypes.number,
  children: PropTypes.node,
};

GalleryItem.displayName = "GalleryItem";

export default GalleryItem;
